import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ClockIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircle2, XCircle, MapPin, ExternalLink, RefreshCw } from 'lucide-react';
import { useToast } from '../../components/ui/use-toast';

const statusOptions = [
  { value: 'all', label: 'Tümü' },
  { value: 'pending_approval', label: 'Ön Onay Bekleyen' },
  { value: 'approved', label: 'Onaylanan' },
  { value: 'cancelled', label: 'İptal Edilen' },
];

const MobileCasesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [showFilters, setShowFilters] = useState(false);
  
  // Auto refresh states
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Auto refresh effect - 3 saniyede bir yenile
  React.useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => {
        queryClient.invalidateQueries(['mobile-cases']);
      }, 3000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, queryClient]);

  // Manuel yenileme fonksiyonu
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries(['mobile-cases']);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Get current filters from URL
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const currentStatus = searchParams.get('status') || 'all';
  const currentSearch = searchParams.get('search') || '';

  // Build query parameters
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.set('page', currentPage.toString());
    params.set('limit', '50');

    if (currentStatus !== 'all') params.set('status', currentStatus);
    if (currentSearch) params.set('search', currentSearch);

    return params.toString();
  };

  // Fetch mobile cases
  const { data: mobileCasesData, isLoading, error } = useQuery({
    queryKey: ['mobile-cases', currentPage, currentStatus, currentSearch],
    queryFn: async () => {
      const response = await api.get(`/mobile-cases?${buildQueryParams()}`);
      console.log('Mobile cases API response:', response.data);
      return response.data;
    },
  });


  const updateSearchParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === 'all') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    // Reset to page 1 when filtering
    if (updates.page === undefined) {
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateSearchParams({ search: searchTerm });
  };

  const handleFilterChange = (filterType, value) => {
    updateSearchParams({ [filterType]: value });
  };

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.post(`/mobile-cases/${id}/approve`);
      console.log('Approve API response:', response.data);
      return response.data;
    },
    onSuccess: (data, id) => {
      const caseNumber = data?.data?.case_number || 'Bilinmiyor';
      toast({
        title: "Başarılı",
        description: `Mobil vaka onaylandı ve normal vakaya dönüştürüldü. Vaka No: ${caseNumber}`,
        variant: "success"
      });
      queryClient.invalidateQueries(['mobile-cases']);
      queryClient.invalidateQueries(['cases']);
    },
    onError: (error) => {
      console.error('Approve error:', error);
      toast({
        title: "Hata",
        description: error.response?.data?.message || error.message || "Mobil vaka onaylanırken hata oluştu",
        variant: "destructive"
      });
    }
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }) => api.post(`/mobile-cases/${id}/cancel`, { cancellation_reason: reason }),
    onSuccess: () => {
      toast({
        title: "Başarılı",
        description: "Mobil vaka iptal edildi",
        variant: "success"
      });
      queryClient.invalidateQueries(['mobile-cases']);
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Mobil vaka iptal edilirken hata oluştu",
        variant: "destructive"
      });
    }
  });


  const handleApprove = (id) => {
    if (window.confirm('Bu mobil vakayı onaylayıp normal vakaya dönüştürmek istediğinize emin misiniz?')) {
      approveMutation.mutate(id);
    }
  };

  const handleCancel = (id) => {
    const reason = window.prompt('İptal sebebini giriniz:');
    if (reason !== null) {
      cancelMutation.mutate({ id, reason });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_approval: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Ön Onay Bekleyen
        </span>
      ),
      approved: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Onaylanan
        </span>
      ),
      cancelled: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          İptal Edilen
        </span>
      ),
    };
    return badges[status] || badges.pending_approval;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    // Use Turkey timezone (Europe/Istanbul) to ensure correct time display
    return date.toLocaleString('tr-TR', {
      timeZone: 'Europe/Istanbul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil((mobileCasesData?.pagination?.total || 0) / 50);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Auto Refresh Controls - Sağ üst köşe */}
      <div className="flex justify-end mb-2">
        <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-1.5 shadow-sm">
          {/* Otomatik Toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-slate-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-slate-800"
            />
            <span className="text-sm text-slate-200 select-none">Otomatik</span>
          </label>
          {/* Manuel Yenile Butonu */}
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="p-1.5 rounded-md hover:bg-slate-700 transition-colors disabled:opacity-50"
            title="Yenile"
          >
            <RefreshCw className={`h-4 w-4 text-slate-300 ${isRefreshing || (autoRefresh && isLoading) ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Mobil Vakalar</h1>
        <p className="text-muted-foreground">
          Mobil uygulamadan gelen ambulans çağrılarını görüntüleyin ve yönetin
        </p>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col space-y-4">
            {/* Search Bar */}
            <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="flex-1">
                <form onSubmit={handleSearch} className="flex space-x-2">
                  <Input
                    placeholder="Kullanıcı adı, soyadı veya çağrı numarası ile arama..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="submit" variant="outline">
                    <MagnifyingGlassIcon className="h-4 w-4" />
                  </Button>
                </form>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2"
              >
                <FunnelIcon className="h-4 w-4" />
                <span>Filtreler</span>
              </Button>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="border-t pt-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Durum</label>
                  <select
                    value={currentStatus}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Mobil Vaka Listesi</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <ClockIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground animate-spin" />
                <p className="text-muted-foreground">Mobil vakalar yükleniyor...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">Hata: {error.message}</p>
              <Button variant="outline" onClick={() => window.location.reload()} className="mt-2">
                Yeniden Dene
              </Button>
            </div>
          ) : !mobileCasesData?.data || mobileCasesData?.data?.length === 0 ? (
            <div className="text-center py-8">
              <MapPinIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">Henüz mobil vaka bulunmuyor</h3>
              <p className="text-muted-foreground">
                Mobil uygulamadan gelen ambulans çağrıları burada görünecektir.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Mobile Cases Table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">#</th>
                      <th className="text-left py-3 px-4 font-medium">Çağrı No</th>
                      <th className="text-left py-3 px-4 font-medium">Kullanıcı</th>
                      <th className="text-left py-3 px-4 font-medium">Telefon</th>
                      <th className="text-left py-3 px-4 font-medium">Kurum</th>
                      <th className="text-left py-3 px-4 font-medium">Konum</th>
                      <th className="text-left py-3 px-4 font-medium">Durum</th>
                      <th className="text-left py-3 px-4 font-medium">Talep Zamanı</th>
                      <th className="text-left py-3 px-4 font-medium">Vaka No</th>
                      <th className="text-left py-3 px-4 font-medium">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mobileCasesData?.data?.map((mobileCase, index) => (
                      <tr key={mobileCase.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-600 font-medium">
                            {(currentPage - 1) * 50 + index + 1}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-mono text-sm text-blue-600">
                            {mobileCase.call_number}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-medium">
                              {mobileCase.user_name} {mobileCase.user_surname}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {mobileCase.user_phone ? (
                              <a 
                                href={`tel:${mobileCase.user_phone.replace(/\s/g, '')}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {mobileCase.user_phone}
                              </a>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {mobileCase.institution_name || '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4 text-gray-500" />
                            <a
                              href={mobileCase.google_maps_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                            >
                              Haritada Gör
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {mobileCase.latitude.toFixed(6)}, {mobileCase.longitude.toFixed(6)}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(mobileCase.status)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {formatDate(mobileCase.request_time)}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {mobileCase.case_number ? (
                            <a
                              href={`/cases/${mobileCase.case_id}`}
                              className="font-mono text-sm text-blue-600 hover:text-blue-800"
                            >
                              #{mobileCase.case_number}
                            </a>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {mobileCase.status === 'pending_approval' && (
                            <div className="flex items-center space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleApprove(mobileCase.id)}
                                disabled={approveMutation.isLoading}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Onayla
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCancel(mobileCase.id)}
                                disabled={cancelMutation.isLoading}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                İptal Et
                              </Button>
                            </div>
                          )}
                          {mobileCase.status === 'approved' && mobileCase.approved_by_name && (
                            <div className="text-xs text-gray-500">
                              Onaylayan: {mobileCase.approved_by_name}
                              <br />
                              {formatDate(mobileCase.approved_at)}
                            </div>
                          )}
                          {mobileCase.status === 'cancelled' && mobileCase.cancelled_by_name && (
                            <div className="text-xs text-gray-500">
                              İptal Eden: {mobileCase.cancelled_by_name}
                              <br />
                              {formatDate(mobileCase.cancelled_at)}
                              {mobileCase.cancellation_reason && (
                                <>
                                  <br />
                                  <span className="text-red-600">Sebep: {mobileCase.cancellation_reason}</span>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <div className="text-sm text-muted-foreground">
                    Sayfa {currentPage} / {totalPages} (Toplam: {mobileCasesData?.pagination?.total || 0} kayıt)
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateSearchParams({ page: currentPage - 1 })}
                      disabled={currentPage === 1}
                    >
                      Önceki
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateSearchParams({ page: currentPage + 1 })}
                      disabled={currentPage >= totalPages}
                    >
                      Sonraki
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MobileCasesPage;

